import { useCallback, useRef, useState } from "react";
import { Hand, Maximize2, Minimize2, Minus, Plus } from "lucide-react";

const MIN = 1;
const MAX = 5;
const clamp = (z) => Math.min(MAX, Math.max(MIN, z));
const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
const mid = (t) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });

// Zoom (Buttons, Mausrad, 2-Finger), Verschieben (Hand-Modus / 2-Finger) und Vollbild für Zeichenflächen.
export function useZoomPan() {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [panMode, setPanMode] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const g = useRef(null);

  const setZoomSafe = useCallback((z) => {
    const nz = clamp(z);
    setZoom(nz);
    if (nz === 1) setPan({ x: 0, y: 0 });
  }, []);

  const point = (e) => (e.touches ? e.touches[0] : e);

  // true = Ereignis ist eine Zoom/Verschiebe-Geste (nicht zeichnen)
  const gestureStart = (e) => {
    if (e.touches && e.touches.length >= 2) {
      e.preventDefault();
      g.current = { type: "pinch", d: dist(e.touches), m: mid(e.touches), zoom, pan };
      return true;
    }
    if (panMode) {
      e.preventDefault();
      const p = point(e);
      g.current = { type: "pan", x: p.clientX, y: p.clientY, pan };
      return true;
    }
    return false;
  };

  const gestureMove = (e) => {
    const s = g.current;
    if (!s) return false;
    e.preventDefault();
    if (s.type === "pinch" && e.touches && e.touches.length >= 2) {
      const m = mid(e.touches);
      const nz = clamp(s.zoom * (dist(e.touches) / s.d));
      setZoom(nz);
      setPan(nz === 1 ? { x: 0, y: 0 } : { x: s.pan.x + (m.x - s.m.x), y: s.pan.y + (m.y - s.m.y) });
    } else if (s.type === "pan") {
      const p = point(e);
      setPan({ x: s.pan.x + (p.clientX - s.x), y: s.pan.y + (p.clientY - s.y) });
    }
    return true;
  };

  const gestureEnd = (e) => {
    if (!g.current) return false;
    if (g.current.type === "pinch" && e?.touches && e.touches.length >= 2) return true;
    g.current = null;
    return true;
  };

  const onWheel = (e) => setZoomSafe(zoom * (e.deltaY < 0 ? 1.15 : 1 / 1.15));

  const transformStyle = {
    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
    transformOrigin: "center center",
    transition: g.current ? "none" : "transform 120ms ease-out",
  };

  return {
    zoom, panMode, setPanMode, fullscreen, setFullscreen, setZoom: setZoomSafe,
    gestureStart, gestureMove, gestureEnd, onWheel, transformStyle,
  };
}

// Bildschirm- → Canvas-Koordinaten (berücksichtigt Zoom/Verschiebung)
export function canvasPos(canvas, e) {
  const rect = canvas.getBoundingClientRect();
  const p = e.touches ? e.touches[0] : e;
  const k = canvas.clientWidth / rect.width;
  return { x: (p.clientX - rect.left) * k, y: (p.clientY - rect.top) * k };
}

const btn = "h-9 w-9 rounded-lg bg-zinc-900/90 border border-zinc-700 text-zinc-100 hover:bg-zinc-800 flex items-center justify-center";

export function ZoomControls({ zp, testid = "zoom" }) {
  return (
    <div className="absolute top-2 right-2 z-20 flex items-center gap-1 rounded-xl bg-black/40 backdrop-blur-md p-1" data-testid={`${testid}-controls`}>
      <button type="button" className={btn} onClick={() => zp.setZoom(zp.zoom / 1.25)} data-testid={`${testid}-out`} title="Verkleinern"><Minus size={16} /></button>
      <button type="button" onClick={() => zp.setZoom(1)} data-testid={`${testid}-reset`} title="Zurücksetzen"
              className="h-9 min-w-[52px] px-1.5 rounded-lg bg-zinc-900/90 border border-zinc-700 text-[12px] font-mono font-bold text-zinc-100">
        {Math.round(zp.zoom * 100)}%
      </button>
      <button type="button" className={btn} onClick={() => zp.setZoom(zp.zoom * 1.25)} data-testid={`${testid}-in`} title="Vergrößern"><Plus size={16} /></button>
      <button type="button" onClick={() => zp.setPanMode(!zp.panMode)} data-testid={`${testid}-pan`} title="Bild verschieben"
              className={`${btn} ${zp.panMode ? "!bg-[#00E575] !text-[#09090B] !border-[#00E575]" : ""}`}><Hand size={16} /></button>
      <button type="button" className={btn} onClick={() => zp.setFullscreen(!zp.fullscreen)} data-testid={`${testid}-fullscreen`}
              title={zp.fullscreen ? "Vollbild beenden" : "Vollbild"}>
        {zp.fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
      </button>
    </div>
  );
}
